import React, { useState } from 'react';
import { UserOrganization, UserRoles } from '@models/user.model';
import { useAppDispatch, useAppSelector } from '@redux/hooks';
import { setOrganizationData } from '@redux/reducers/organization.reducer';
import { useSetDefaultOrganization, useCurrentUser } from '@hooks/api';
import { RootState } from '@redux/store';
import { CheckCircle2, Ban, AlertTriangle, Clock } from 'lucide-react';
import { TRIAL_URGENT_THRESHOLD_DAYS } from '@hooks/useTrialStatus';
import { OrganizationStatus } from '@models/organization.model';
import ReactivateSubscriptionModal from '@pages/organizationSuspended/components/ReactivateSubscriptionModal';
import { OrgPaymentAccountSection } from './OrgPaymentAccountSection';

interface OrganizationCardProps {
  userOrganization: UserOrganization;
}

export const OrganizationCard: React.FC<OrganizationCardProps> = ({
  userOrganization,
}) => {
  const dispatch = useAppDispatch();
  const { organization, role, userStatus, subscriptionStatus, trialEndsAt } =
    userOrganization;
  const { mutateAsync: setDefaultOrganization, isPending } =
    useSetDefaultOrganization();
  const { data: currentUser } = useCurrentUser();
  const [showReactivateModal, setShowReactivateModal] = useState(false);

  // Get current organization from Redux to show which one is active
  const currentOrganizationId = useAppSelector(
    (state: RootState) => state.organization.organizationId,
  );

  // Get user data from Redux for the reactivate modal
  const userData = useAppSelector((state: RootState) => state.user);

  const isDefault =
    currentUser?.getSelf?.defaultOrganizationId === organization.organizationId;
  const isActive = currentOrganizationId === organization.organizationId;
  const isUserDisabled = userStatus === 'DISABLED';
  const isOrgSuspended = organization.status === OrganizationStatus.SUSPENDED;
  const isOrgDisabled = organization.status === OrganizationStatus.DISABLED;
  const isOwner = role === UserRoles.OWNER;
  // Reviewers are read-only and must never see the organization payment account.
  const isReviewer = role === UserRoles.REVIEWER;

  // Disable actions if user is disabled OR organization is suspended/disabled
  const actionsDisabled = isUserDisabled || isOrgSuspended || isOrgDisabled;

  const handleSwitchOrganization = async () => {
    // Set the organization in Redux (which will update the header for GraphQL requests)
    dispatch(setOrganizationData(organization));

    // Small delay to ensure redux-persist saves the state before navigation
    await new Promise((resolve) => setTimeout(resolve, 100));

    // Navigate to home page to get a clean slate with new organization context
    // This is better than reload() because it ensures React Query cache is fresh
    window.location.href = '/';
  };

  const handleSetDefault = async () => {
    await setDefaultOrganization(organization.organizationId);
  };

  // Get initials for avatar
  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map((word) => word[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  // Get color for avatar based on organization name
  const getAvatarColor = (name: string) => {
    const colors = [
      'bg-indigo-600',
      'bg-purple-600',
      'bg-pink-600',
      'bg-blue-600',
      'bg-green-600',
      'bg-yellow-600',
      'bg-teal-600',
    ];
    const index = name.charCodeAt(0) % colors.length;
    return colors[index];
  };

  // Get card background and border styles based on status
  const getCardStyles = () => {
    if (isUserDisabled) {
      return 'bg-gray-100 dark:bg-gray-900 border-gray-300 dark:border-gray-700 opacity-75';
    }
    if (isOrgSuspended) {
      return 'bg-amber-50 dark:bg-amber-950/30 border-amber-400 dark:border-amber-700 border-2';
    }
    if (isOrgDisabled) {
      return 'bg-red-50 dark:bg-red-950/30 border-red-300 dark:border-red-800 opacity-75';
    }
    if (isActive) {
      return 'bg-indigo-50/50 dark:bg-indigo-950/30 border-indigo-300 dark:border-indigo-800 hover:shadow-lg';
    }
    return 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 hover:border-indigo-200 dark:hover:border-indigo-700 hover:shadow-lg';
  };

  // Get avatar background color
  const getAvatarBgColor = () => {
    if (isUserDisabled || isOrgDisabled) return 'bg-gray-400';
    if (isOrgSuspended) return 'bg-amber-500';
    return getAvatarColor(organization.name);
  };

  /**
   * Trial state for THIS organization.
   *
   * A user can own several organizations at once, each on its own clock, so the badge
   * lives per card rather than in the sidebar — the sidebar only ever knows about the one
   * currently open. Suppressed while the organization is suspended or disabled, where a
   * countdown would compete with a more urgent message.
   */
  const renderTrialBadge = () => {
    if (isOrgSuspended || isOrgDisabled || isUserDisabled) return null;

    if (subscriptionStatus === 'TRIAL_EXPIRED') {
      return (
        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300">
          <AlertTriangle size={12} />
          Trial ended — read only
        </span>
      );
    }

    if (subscriptionStatus !== 'TRIAL' || !trialEndsAt) return null;

    const msPerDay = 24 * 60 * 60 * 1000;
    // Ceil so a partial day still reads as a day left — "0 days" while the user can
    // still write would be wrong.
    const daysRemaining = Math.max(
      0,
      Math.ceil((new Date(trialEndsAt).getTime() - Date.now()) / msPerDay),
    );
    // Same threshold as the sidebar pill and the dashboard banner. Hardcoding 7 here made
    // this card turn amber a week before the other two surfaces did, so the same
    // organization showed two different urgency levels on screen at once.
    const urgent = daysRemaining <= TRIAL_URGENT_THRESHOLD_DAYS;

    return (
      <span
        className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${
          urgent
            ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300'
            : 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'
        }`}>
        <Clock size={12} />
        {daysRemaining === 0
          ? 'Trial ends today'
          : daysRemaining === 1
            ? 'Trial: 1 day left'
            : `Trial: ${daysRemaining} days left`}
      </span>
    );
  };

  // Get status badge
  const renderStatusBadge = () => {
    if (isUserDisabled) {
      return (
        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400">
          <Ban size={12} />
          You are disabled
        </span>
      );
    }
    if (isOrgSuspended) {
      return (
        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300 border border-amber-300 dark:border-amber-700">
          <AlertTriangle size={12} />
          Suspended
        </span>
      );
    }
    if (isOrgDisabled) {
      return (
        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400">
          <Ban size={12} />
          Disabled
        </span>
      );
    }
    if (organization.status === OrganizationStatus.ENABLED) {
      return (
        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
          <CheckCircle2 size={12} />
          Active
        </span>
      );
    }
    return null;
  };

  return (
    <div
      data-testid={`org-card-${organization.organizationId}`}
      className={`border rounded-xl p-6 transition-all duration-200 ${getCardStyles()}`}>
      {/* Suspended Warning Banner */}
      {isOrgSuspended && (
        <div className="flex items-center gap-2 mb-4 p-3 bg-amber-100 dark:bg-amber-900/30 border border-amber-300 dark:border-amber-700 rounded-lg">
          <AlertTriangle size={18} className="text-amber-600 dark:text-amber-400 flex-shrink-0" />
          <p className="text-sm text-amber-800 dark:text-amber-200">
            This organization is suspended. Contact the organization owner to reactivate the subscription.
          </p>
        </div>
      )}

      <div className="flex items-start justify-between">
        <div className="flex items-start gap-4 flex-1">
          {/* Avatar */}
          <div
            className={`w-14 h-14 rounded-full ${getAvatarBgColor()} flex items-center justify-center text-white font-bold text-xl shadow-md`}>
            {getInitials(organization.name)}
          </div>

          {/* Organization Info */}
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2 flex-wrap">
              <h3
                className={`text-lg font-bold ${
                  actionsDisabled
                    ? 'text-gray-500 dark:text-gray-400'
                    : 'text-gray-900 dark:text-white'
                }`}>
                {organization.name}
              </h3>
              {renderStatusBadge()}
              {renderTrialBadge()}
              {isActive && !actionsDisabled && (
                <span className="px-2.5 py-1 text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300 rounded-full">
                  Current
                </span>
              )}
              {isDefault && !actionsDisabled && (
                <span className="px-2.5 py-1 text-xs font-medium bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300 rounded-full">
                  Default
                </span>
              )}
            </div>

            {/* Role Badge */}
            <div
              className={`inline-flex items-center gap-2 px-3 py-1 rounded-lg text-xs font-medium ${
                actionsDisabled
                  ? 'bg-gray-200 text-gray-500 dark:bg-gray-700 dark:text-gray-400'
                  : 'bg-indigo-50 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300'
              }`}>
              <div
                className={`w-2 h-2 rounded-full ${
                  actionsDisabled ? 'bg-gray-400' : 'bg-indigo-500'
                }`}></div>
              {role}
            </div>

            {/* Help messages */}
            {isUserDisabled && (
              <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                Contact the organization admin to regain access
              </p>
            )}
            {isOrgDisabled && !isUserDisabled && (
              <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                This organization has been disabled
              </p>
            )}
          </div>
        </div>

        {/* Actions - hidden when user is disabled or org is suspended/disabled */}
        <div className="flex items-center gap-2">
          {/* Reactivate button for suspended orgs owned by OWNER */}
          {isOrgSuspended && isOwner && (
            <button type="button"
              onClick={() => setShowReactivateModal(true)}
              data-testid={`org-card-reactivate-${organization.organizationId}`}
              className="px-4 py-2 bg-amber-600 text-white rounded-lg text-sm font-medium hover:bg-amber-700 transition">
              Reactivate
            </button>
          )}
          {!isDefault && !actionsDisabled && (
            <button type="button"
              onClick={handleSetDefault}
              disabled={isPending}
              data-testid={`org-card-set-default-${organization.organizationId}`}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition disabled:opacity-50 disabled:cursor-not-allowed">
              {isPending ? 'Setting...' : 'Set as Default'}
            </button>
          )}
          {!isActive && !actionsDisabled && (
            <button type="button"
              onClick={handleSwitchOrganization}
              data-testid={`org-card-switch-${organization.organizationId}`}
              className="px-4 py-2 bg-indigo-900 dark:bg-indigo-700 text-white rounded-lg text-sm font-medium hover:bg-indigo-800 dark:hover:bg-indigo-600 transition">
              Switch
            </button>
          )}
        </div>
      </div>

      {/* Payment account — hidden from reviewers; only mount the data hook for the active org */}
      {!isReviewer &&
        !actionsDisabled &&
        (isActive ? (
          <OrgPaymentAccountSection />
        ) : (
          <p className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700 text-sm text-gray-500 dark:text-gray-400">
            Switch to this organization to manage its payment account.
          </p>
        ))}

      {/* Reactivate Subscription Modal */}
      <ReactivateSubscriptionModal
        isOpen={showReactivateModal}
        onClose={() => setShowReactivateModal(false)}
        organizationId={organization.organizationId}
        organizationName={organization.name}
        userData={{
          firstName: userData?.firstName || undefined,
          lastName: userData?.lastName || undefined,
          email: userData?.email || undefined,
        }}
        skipRedirect={true}
      />
    </div>
  );
};
